import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { usePageMeta, useSite } from "@/lib/store";
import { WRAP } from "@/components/store/Chrome";
import { ProductGrid } from "@/components/store/ProductCard";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function useHeading(params, site, gear) {
  const mood = (site?.mood_groups || []).flatMap((g) => g.moods).find((m) => m.id === params.get("mood"));
  if (gear) return ["Turntables & Accessories", params.get("cat") || "Turntables & Accessories"];
  if (params.get("q")) return ["Search results", `“${params.get("q")}”`];
  if (params.get("artist")) return ["Artist", params.get("artist")];
  if (params.get("genre")) return ["Genre", params.get("genre")];
  if (mood) return ["Shop by mood", mood.name];
  if (params.get("sale")) return ["Price drops", "Sale"];
  if (params.get("view") === "new") return ["Just racked", "New In"];
  return ["The vault", "All Records"];
}

function GearChips({ cat, onPick }) {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/gear-categories").then((r) => setCats(r.data));
  }, []);
  const chip = (on) => `px-4 py-2 text-xs font-bold uppercase tracking-[0.06em] border transition-colors ${on ? "bg-black text-white border-black" : "bg-white border-[#ccc] hover:border-black"}`;
  return (
    <div className="flex flex-wrap gap-2 mb-8 font-arial" data-testid="gear-chips">
      <button onClick={() => onPick("")} className={chip(!cat)} data-testid="gear-chip-all">All</button>
      {cats.map((c) => <button key={c.name} onClick={() => onPick(c.name)} className={chip(cat === c.name)} data-testid={`gear-chip-${c.name.replace(/\W+/g, "-").toLowerCase()}`}>{c.name}</button>)}
    </div>
  );
}

export default function Catalogue({ gear = false }) {
  const [params, setParams] = useSearchParams();
  const { site } = useSite();
  const [data, setData] = useState(null);
  const page = Number(params.get("page") || 1);
  const sort = params.get("sort") || "new";
  const [label, title] = useHeading(params, site, gear);
  usePageMeta(title === "All Records" ? site?.pages?.catalogue?.title : `${title} | Vinyl Record Vault`, site?.pages?.catalogue?.description, site?.pages?.catalogue?.keywords);

  useEffect(() => {
    setData(null);
    api.get("/products", {
      params: gear
        ? { kind: "gear", cat: params.get("cat") || "", sort, page }
        : { q: params.get("q") || "", artist: params.get("artist") || "", genre: params.get("genre") || "", mood: params.get("mood") || "", sale: !!params.get("sale"), sort, page },
    }).then((r) => setData(r.data));
    window.scrollTo(0, 0);
  }, [params, sort, page, gear]);

  const set = (k, v) => {
    const n = new URLSearchParams(params);
    v ? n.set(k, v) : n.delete(k);
    if (k !== "page") n.delete("page");
    setParams(n);
  };

  return (
    <div className={`${WRAP} pt-12`}>
      <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-black pb-4 mb-8">
        <div>
          <p className="label-caps text-[11px] text-red mb-1.5">{label}</p>
          <h1 className="font-head font-extrabold text-4xl sm:text-5xl tracking-tight" data-testid="catalogue-title">{title}</h1>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-[#777] font-arial" data-testid="catalogue-count">{data ? `${data.total.toLocaleString("en-IN")} ${gear || params.get("q") ? "items" : "records"}` : "…"}</span>
          <Select value={sort} onValueChange={(v) => set("sort", v)}>
            <SelectTrigger className="w-44 rounded-none bg-white" data-testid="catalogue-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent className="rounded-none">
              <SelectItem value="new">Newest first</SelectItem>
              <SelectItem value="price_asc">Price: low to high</SelectItem>
              <SelectItem value="price_desc">Price: high to low</SelectItem>
              <SelectItem value="artist">{gear ? "Brand A–Z" : "Artist A–Z"}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {gear && <GearChips cat={params.get("cat") || ""} onPick={(c) => set("cat", c)} />}
      {!data && <div className="h-96 animate-pulse bg-white/60" />}
      {data && data.items.length === 0 && (
        <p className="py-24 text-center text-[#777]" data-testid="catalogue-empty">{gear ? "Nothing here yet." : "No records found. Try another search or genre."}</p>
      )}
      {data && <ProductGrid products={data.items} testid="catalogue-grid" />}
      {data && data.pages > 1 && (
        <div className="flex items-center justify-center gap-3 mt-14 font-arial text-sm" data-testid="catalogue-pagination">
          <button disabled={page <= 1} onClick={() => set("page", page - 1)} className="px-4 py-2 border border-black disabled:opacity-30 hover:bg-black hover:text-white transition-colors" data-testid="catalogue-prev">Previous</button>
          <span>Page {page} of {data.pages.toLocaleString("en-IN")}</span>
          <button disabled={page >= data.pages} onClick={() => set("page", page + 1)} className="px-4 py-2 border border-black disabled:opacity-30 hover:bg-black hover:text-white transition-colors" data-testid="catalogue-next">Next</button>
        </div>
      )}
    </div>
  );
}
